import { useState, type ReactNode } from 'react';
import { type OrgAssetResponse } from '@goldcast/api/content';
import Button from '@/components/atoms/Button/Button';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import { classnames } from '@/libs/utils';
import IconButton from '@/components/atoms/Button/IconButton';

const PAGE_SIZE = 3;

interface OverlayUploadSectionProps {
  isAdding: boolean;
  uploadTitle: string;
  uploadLabel: string;
  accept: string;
  onFilesUpload: (files: File[]) => void;
  recentAssets: OrgAssetResponse[];
  onAssetClick: (asset: OrgAssetResponse) => void;
  renderAssetContent: (asset: OrgAssetResponse) => ReactNode;
  testIdPrefix: string;
}

const OverlayUploadSection = ({
  isAdding,
  uploadTitle,
  uploadLabel,
  accept,
  onFilesUpload,
  recentAssets,
  onAssetClick,
  renderAssetContent,
  testIdPrefix
}: OverlayUploadSectionProps) => {
  const [page, setPage] = useState(0);
  const showArrows = recentAssets.length > PAGE_SIZE;
  const maxPage = Math.ceil(recentAssets.length / PAGE_SIZE) - 1;
  const pageAssets = recentAssets.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
      <div data-testid={`${testIdPrefix}-upload-input`}>
        <FilesInput
          onFilesUpload={onFilesUpload}
          accept={accept}
          title={isAdding ? 'Uploading...' : uploadTitle}
          label={uploadLabel}
          multiple={false}
          iconClassNames={isAdding ? 'animate-spin' : ''}
          icon={isAdding ? 'IconLoader2' : 'IconUpload'}
          isDisabled={isAdding}
          containerClassNames="border-dashed !py-2"
          labelClassNames="max-w-80 text-xs leading-[18px] font-medium text-slate-400"
          titleClassNames="text-sm text-slate-500 font-semibold"
        />
      </div>

      {recentAssets.length > 0 && (
        <div
          data-testid={`recently-used-${testIdPrefix}`}
          className={classnames('transition-opacity', isAdding ? 'pointer-events-none opacity-50' : '')}
        >
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium text-black">Recently used</h3>
            {showArrows && (
              <div className="flex gap-1">
                <IconButton
                  icon="IconChevronLeft"
                  trackingId={`recently-used-prev-${testIdPrefix}`}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  variation="none"
                  buttonClassName="rounded p-0.5 text-black hover:text-gray-800 disabled:opacity-30"
                />
                <IconButton
                  icon="IconChevronRight"
                  trackingId={`recently-used-next-${testIdPrefix}`}
                  onClick={() => setPage(p => Math.min(maxPage, p + 1))}
                  disabled={page === maxPage}
                  variation="none"
                  buttonClassName="rounded p-0.5 text-black hover:text-gray-800 disabled:opacity-30"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-[10px]">
            {pageAssets.map(asset => (
              <Button
                key={asset.id}
                trackingId={`recently-used-asset-${asset.id}`}
                onClick={() => onAssetClick(asset)}
                variation="none"
                className="flex h-[110px] flex-col overflow-hidden rounded-lg border border-slate-200 !p-0 text-left hover:border-card-active-border"
              >
                <div className="w-full flex-1 overflow-hidden rounded-t-lg bg-slate-100">
                  {renderAssetContent(asset)}
                </div>
                <span className="w-full truncate rounded-b-lg px-1.5 py-1 text-xs text-gray-700">{asset.name}</span>
              </Button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default OverlayUploadSection;
